import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Download, Copy, Share2, Palette, Trash2, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { GenerationResult } from '@/utils/visualGenerationHandlers';
import { toast } from 'sonner';

interface BatchGenerationCardProps {
  result: GenerationResult;
  onPreview?: (imageUrl: string) => void;
  onDownload?: (imageUrl: string, filename: string) => void;
  onUseAsInput?: (imageUrl: string) => void;
  onSetAsPreview?: (result: GenerationResult) => void;
  onDelete?: (result: GenerationResult) => void;
  className?: string;
}

export const BatchGenerationCard: React.FC<BatchGenerationCardProps> = ({
  result,
  onPreview,
  onDownload,
  onUseAsInput,
  onSetAsPreview,
  onDelete,
  className
}) => {
  const allImageUrls = result.imageUrls || [];
  const purposeInfo = result.purposeMetadata;

  const handleDownloadAll = () => {
    try {
      // Download all images
      allImageUrls.forEach((imageUrl, index) => {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `${purposeInfo?.platform || 'generated'}-batch-${index + 1}-${Date.now()}.png`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
      
      toast.success(`Download started for ${allImageUrls.length} images`);
      onDownload?.(allImageUrls[0], `batch-${allImageUrls.length}-images`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try right-clicking and saving the images.');
    }
  };

  const handleDeleteBatch = () => {
    onDelete?.(result);
  };

  const handlePreviewGallery = () => {
    // For now, preview the first image - could enhance to show gallery
    onPreview?.(allImageUrls[0]);
  };

  return (
    <div className={cn("relative group", className)}>
      {/* Batch Images Grid */}
      <div className="relative">
        <div className="w-full h-56 rounded-lg overflow-hidden">
          <div className={cn(
            "grid gap-1 h-full",
            allImageUrls.length === 1 ? "grid-cols-1" :
            allImageUrls.length === 2 ? "grid-cols-2" :
            allImageUrls.length === 3 ? "grid-cols-3" :
            allImageUrls.length === 4 ? "grid-cols-2 grid-rows-2" :
            "grid-cols-3 grid-rows-2"
          )}>
            {allImageUrls.slice(0, 6).map((imageUrl, index) => (
              <img
                key={index}
                src={imageUrl}
                alt={`Batch variant ${index + 1}`}
                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => onPreview?.(imageUrl)}
              />
            ))}
            {allImageUrls.length > 6 && (
              <div className="bg-black/70 flex items-center justify-center text-white text-sm font-medium">
                +{allImageUrls.length - 6}
              </div>
            )}
          </div>
        </div>
        
        {/* Batch Generation Badge */}
        <div className="absolute top-2 left-2 z-10">
          <Badge variant="secondary" className="text-xs bg-white/90 text-gray-800">
            {allImageUrls.length} variants
          </Badge>
        </div>
        
        {/* Centered action icons overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handlePreviewGallery}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <Eye className="h-5 w-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Preview batch ({allImageUrls.length} images)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleDownloadAll}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <Download className="h-5 w-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Download all {allImageUrls.length} images</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onUseAsInput?.(allImageUrls[0])}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <Copy className="h-5 w-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Use first image as input</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Set as Preview button - use first result */}
          {result.prompt && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onSetAsPreview?.(result)}
                    className="p-2 bg-blue-500/80 hover:bg-blue-500 rounded-full transition-colors"
                  >
                    <Camera className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Set first image as preview</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleDeleteBatch}
                  className="p-2 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                >
                  <Trash2 className="h-5 w-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete entire batch</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};